import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  Headphones, ArrowRight, CheckCircle, Truck, Wrench,
  Search, Shield, Clipboard, Phone, Car, FileText,
  TrendingUp, Clock
} from 'lucide-react'

// Curated collector car images (Unsplash CDN)
const HERO_IMG = 'https://images.unsplash.com/photo-1534093607318-f025413f49cb?w=2070&auto=format&fit=crop&q=80'
const CONCIERGE_IMG = 'https://images.unsplash.com/photo-1484687742385-1249620c2687?w=2070&auto=format&fit=crop&q=80'

const CheckItem = ({ text }) => (
  <div className="flex items-start gap-3">
    <CheckCircle size={20} className="text-rdc-orange flex-shrink-0 mt-0.5" />
    <span className="text-rdc-charcoal">{text}</span>
  </div>
)

export default function Welcome() {
  const { user, loading } = useAuth()

  if (!loading && user) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="min-h-screen bg-rdc-cream">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-rdc-stone/20">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-3">
            <img
              src="/RDC_Logomark_Orange.png"
              alt="Riding & Driving Club"
              className="h-10 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium text-rdc-charcoal hover:text-black transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="px-5 py-2 text-sm font-medium bg-rdc-orange text-white rounded-full hover:bg-rdc-orange/90 transition-colors"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero — Concierge-first messaging */}
      <section className="relative overflow-hidden min-h-[500px] md:min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/65 to-black/30" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-24 md:py-36">
          <div className="max-w-2xl">
            <p className="text-rdc-orange font-semibold text-sm tracking-[0.2em] uppercase mb-4">
              Concierge for Collectors
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              You Drive.{' '}
              <span className="text-rdc-orange">We Handle Everything Else.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 leading-relaxed mb-10 max-w-xl">
              Enjoy your cars. We'll manage the transport, service, insurance, inspections, and documentation — one dedicated team that handles the logistics of collector car ownership so you don't have to.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-8 py-4 bg-rdc-orange text-white font-semibold rounded-full hover:bg-rdc-orange/90 transition-colors text-lg shadow-lg"
              >
                Start a Request
                <ArrowRight size={20} />
              </Link>
              <a
                href="mailto:michael@ridinganddrivingclub.com"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 backdrop-blur-sm text-white font-semibold rounded-full border-2 border-white/30 hover:bg-white/25 transition-colors text-lg"
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="border-b border-rdc-stone/20 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <p className="text-center text-sm text-rdc-taupe">
            Based in Millbrook, NY — serving collectors of classics, exotics, and everything worth protecting
          </p>
        </div>
      </section>

      {/* What We Handle — Services with image */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left — Services list */}
          <div>
            <p className="text-rdc-orange font-semibold text-sm tracking-[0.2em] uppercase mb-4">
              What We Handle
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-black mb-4">
              Tell Us What You Need.{' '}
              <span className="text-rdc-orange">We Take It From There.</span>
            </h2>
            <p className="text-rdc-charcoal text-lg mb-8">
              Every request becomes a managed work order. You approve each step. We handle the rest.
            </p>

            <div className="space-y-4">
              {[
                { icon: Truck, label: 'Transport', sub: 'Enclosed carriers, coast to coast, door to door' },
                { icon: Wrench, label: 'Service & Repair', sub: 'Vetted marque specialists, scheduled and managed' },
                { icon: Search, label: 'Pre-Purchase Inspections', sub: 'Eyes on any car, any market, before you commit' },
                { icon: Shield, label: 'Insurance', sub: 'Agreed-value placement with collector specialists' },
                { icon: Clipboard, label: 'Restoration Oversight', sub: 'Budget tracking, milestone updates, quality checks' },
                { icon: Phone, label: 'On-Call Support', sub: 'Direct line — no phone trees, no waiting' },
              ].map(({ icon: Icon, label, sub }, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 bg-white rounded-xl border border-rdc-stone/15 p-4 shadow-sm"
                >
                  <div className="w-10 h-10 rounded-full bg-rdc-orange/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-rdc-orange" />
                  </div>
                  <div>
                    <p className="font-display font-bold text-black">{label}</p>
                    <p className="text-sm text-rdc-charcoal mt-0.5">{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Image + how it works */}
          <div className="space-y-8">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src={CONCIERGE_IMG}
                alt="Classic Jaguar"
                className="w-full h-[340px] object-cover object-center"
              />
            </div>

            {/* How it works — compact */}
            <div className="bg-rdc-charcoal rounded-2xl p-6">
              <p className="text-rdc-orange font-semibold text-xs tracking-[0.15em] uppercase mb-4">How it works</p>
              <div className="space-y-4">
                {[
                  { step: '1', text: 'Describe what you need — transport, service, inspection, anything.' },
                  { step: '2', text: 'We create a work order, get quotes, and present your options.' },
                  { step: '3', text: 'You approve. We coordinate everything through completion.' },
                ].map(({ step, text }, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-rdc-orange/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-rdc-orange text-xs font-bold">{step}</span>
                    </div>
                    <p className="text-white/80 text-sm">{text}</p>
                  </div>
                ))}
              </div>

              {/* Chat preview */}
              <div className="mt-6 pt-5 border-t border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-rdc-orange flex items-center justify-center">
                    <Headphones size={12} className="text-white" />
                  </div>
                  <p className="font-semibold text-white text-xs">RDC Concierge</p>
                </div>
                <div className="space-y-2">
                  <div className="bg-white/10 rounded-lg rounded-bl-sm px-3 py-2">
                    <p className="text-xs text-white/75">
                      I need to get my '67 Mustang from Millbrook to Amelia Island for the concours. Enclosed only.
                    </p>
                  </div>
                  <div className="bg-rdc-orange/20 rounded-lg rounded-br-sm px-3 py-2 ml-4">
                    <p className="text-xs text-white/75">
                      Got it — work order created for enclosed transport. I'll have quotes and pickup windows within 24 hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Garage — Secondary feature */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <p className="text-rdc-orange font-semibold text-sm tracking-[0.2em] uppercase mb-4">
            Included Free
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-black mb-4">
            Your Digital Garage
          </h2>
          <p className="text-rdc-charcoal text-lg max-w-2xl mx-auto">
            Every concierge client gets a digital garage — a single place to track, document, and protect your collection.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Car, title: 'Vehicle Profiles', desc: 'Photos, specs, history, and ownership records for every car in your collection.' },
            { icon: FileText, title: 'Document Vault', desc: 'Titles, registrations, invoices, and appraisals — stored securely, accessible anywhere.' },
            { icon: TrendingUp, title: 'AI Valuations', desc: 'Real-time market estimates based on comparable sales, condition, and provenance.' },
            { icon: Clock, title: 'Service Tracking', desc: 'Complete maintenance history with reminders for upcoming service intervals.' },
          ].map(({ icon: Icon, title, desc }, i) => (
            <div key={i} className="bg-white rounded-xl p-6 border border-rdc-stone/15 shadow-sm">
              <div className="w-10 h-10 rounded-full bg-rdc-orange/10 flex items-center justify-center mb-4">
                <Icon size={20} className="text-rdc-orange" />
              </div>
              <h3 className="font-display font-bold text-black mb-2">{title}</h3>
              <p className="text-sm text-rdc-charcoal leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing — Clear rate card */}
      <section className="bg-white border-y border-rdc-stone/20">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-black mb-4">
              Pay Only When You Need Us
            </h2>
            <p className="text-rdc-charcoal text-lg max-w-2xl mx-auto">
              The digital garage is free forever. Concierge services are vendor cost + a 20% service fee — no subscriptions, no hidden charges.
            </p>
          </div>

          {/* Free tier callout */}
          <div className="bg-rdc-cream rounded-2xl p-6 mb-8 max-w-4xl mx-auto border border-rdc-stone/15">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rdc-orange/10 flex items-center justify-center">
                  <Car size={20} className="text-rdc-orange" />
                </div>
                <div>
                  <p className="font-display font-bold text-black">Digital Garage — Free Forever</p>
                  <p className="text-xs text-rdc-charcoal">Unlimited vehicles, document vault, service tracking, AI valuations, maintenance reminders</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-black ml-auto">$0</p>
            </div>
          </div>

          {/* Concierge pricing */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-rdc-charcoal to-rdc-charcoal/95 rounded-2xl p-8 border border-rdc-orange/20">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-rdc-orange/20 flex items-center justify-center">
                  <Headphones size={20} className="text-rdc-orange" />
                </div>
                <div>
                  <p className="font-display font-bold text-white">Concierge Services — Pay As You Go</p>
                  <p className="text-xs text-rdc-stone">Simple, transparent pricing on every engagement.</p>
                </div>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <p className="text-4xl font-bold text-white">20%</p>
                <p className="text-white/60 text-lg">service fee</p>
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-6 max-w-lg">
                You pay the vendor's price for any service — transport, maintenance, inspections, insurance, restoration — plus a flat 20% coordination fee. That's it. No subscriptions, no hidden costs.
              </p>
              <div className="grid sm:grid-cols-3 gap-3 mb-6">
                {[
                  { icon: Truck, label: 'Transport' },
                  { icon: Wrench, label: 'Service & Repair' },
                  { icon: Search, label: 'Inspections' },
                  { icon: Shield, label: 'Insurance' },
                  { icon: Clipboard, label: 'Restoration' },
                  { icon: Phone, label: 'On-Call Support' },
                ].map(({ icon: Icon, label }, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/[0.06] rounded-lg px-3 py-2">
                    <Icon size={16} className="text-rdc-orange flex-shrink-0" />
                    <span className="text-white/80 text-sm">{label}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 text-rdc-orange text-sm font-semibold hover:text-rdc-orange/80 transition-colors"
              >
                Full pricing details & FAQ
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Beta CTA */}
      <section className="bg-gradient-to-br from-rdc-charcoal to-black">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            We're Accepting a Limited Number of Beta Members
          </h2>
          <p className="text-white/70 text-lg mb-10 max-w-xl mx-auto">
            RDC is in early access, working with a small group of collectors in the Hudson Valley and beyond. Sign up to get started — or reach out directly.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-rdc-orange text-white font-semibold rounded-full hover:bg-rdc-orange/90 transition-colors text-lg shadow-lg"
            >
              Create Free Account
              <ArrowRight size={20} />
            </Link>
            <a
              href="mailto:michael@ridinganddrivingclub.com"
              className="inline-flex items-center gap-2 px-8 py-4 bg-transparent text-white font-semibold rounded-full border-2 border-white/40 hover:bg-white/10 transition-colors text-lg"
            >
              michael@ridinganddrivingclub.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-rdc-taupe text-white/70">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src="/RDC_Logomark_Orange.png"
                alt="RDC"
                className="h-8 w-auto brightness-0 invert opacity-70"
              />
              <span className="font-display font-bold text-white">Riding & Driving Club</span>
            </div>
            <div className="flex items-center gap-6 text-sm flex-wrap justify-center md:justify-end">
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
            </div>
            <p className="text-sm">&copy; {new Date().getFullYear()} Riding & Driving Club</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
